import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBookingContext } from '../context/BookingContext';
import { useStoreSelector } from '../hooks/useStoreSelector';
import useBookingStore, {
  selectStayData,
  selectStayId,
  selectCheckIn,
  selectCheckOut,
  selectNights,
  selectGuests,
  selectRoomTypeId,
  selectRoomCount,
  selectPricingBreakdown,
  selectPaymentOptionSelected,
  selectSetStayData,
  selectSetPaymentOptionSelected,
} from '../store/bookingStore';
import { confirmBooking, getBookingById } from '../api/booking';
import { getCheckoutStayData } from '../api/propertyDetail';
import { normalizeBookingDraft } from '../utils/bookingMapper';
import { requireBookingId, requireGuestComplete } from '../utils/bookingGuards';
import { formatDateRange, formatGuests, formatInr, formatShortDate } from '../utils/bookingUtils';
import { getTaxFeeLineItems } from '../utils/taxFeeBreakdown';
import { normalizeBookingPricing } from '../../utils/bookingPricing';
import MobileStickyBar from '../components/cart/MobileStickyBar';
import '../../mobile/styles/mobile-theme.css';

const normalizeBookingMode = (value, hasRoomTypes, hasRoomSelection) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['room', 'room_wise', 'roomwise', 'by_room', 'per_room'].includes(normalized)) return 'room';
  if (['full', 'full_property', 'entire', 'entire_property', 'whole', 'whole_property'].includes(normalized)) {
    return 'full_property';
  }
  if (hasRoomSelection) return 'room';
  return hasRoomTypes ? 'room' : 'full_property';
};

const normalizeTextList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const normalizeSectionTitle = (title) =>
  String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const resolveHouseRulesTitle = (title) => {
  const normalized = normalizeSectionTitle(title);
  if (normalized === normalizeSectionTitle('Smoking/Alcohol consumption Rules')) {
    return 'Smoking & Alcohol Consumption Rules';
  }
  return title;
};

const normalizeHouseRulesSections = (value, fallback) => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return normalizeHouseRulesSections(parsed, fallback);
    } catch {
      // ignore parse errors
    }
  }
  if (Array.isArray(value)) {
    const cleaned = value
      .map((section) => {
        if (!section) return null;
        const rawTitle = String(section.title || section.label || section.name || '').trim();
        const title = resolveHouseRulesTitle(rawTitle);
        const items = normalizeTextList(section.items || section.rules || section.content || section.text || section.notes);
        if (!title && !items.length) return null;
        return { title: title || 'House Rules', items };
      })
      .filter(Boolean);
    if (cleaned.length) return cleaned;
  }
  const fallbackItems = normalizeTextList(fallback);
  if (fallbackItems.length) {
    return [{ title: 'House Rules & Information', items: fallbackItems }];
  }
  return [];
};

const mergeHouseRulesFromPolicies = (sections, { petPolicy, smokingPolicy, idRequirement, guestProfile } = {}) => {
  const list = (Array.isArray(sections) ? sections : []).map((section) => ({
    title: resolveHouseRulesTitle(String(section?.title || '').trim()),
    items: Array.isArray(section?.items) ? [...section.items] : [],
  }));
  const apply = (title, value, aliases = []) => {
    const items = normalizeTextList(value);
    if (!items.length) return;
    const targets = [title, ...aliases].map(normalizeSectionTitle);
    const matchIndex = list.findIndex((section) => targets.includes(normalizeSectionTitle(section?.title || '')));
    if (matchIndex >= 0) {
      const existing = list[matchIndex];
      const mergedItems = Array.isArray(existing.items) ? [...existing.items] : [];
      items.forEach((item) => {
        if (!mergedItems.some((entry) => String(entry).toLowerCase() === String(item).toLowerCase())) {
          mergedItems.push(item);
        }
      });
      list[matchIndex] = { ...existing, title, items: mergedItems };
    } else {
      list.push({ title, items: [...items] });
    }
  };
  apply('Pet(s) Related', petPolicy);
  apply('Smoking & Alcohol Consumption Rules', smokingPolicy, ['Smoking/Alcohol consumption Rules']);
  apply('Guest Profile', guestProfile);
  apply('ID Proof Related', idRequirement);
  return list;
};

const dedupeTextList = (items) => {
  const seen = new Set();
  const output = [];
  items.forEach((item) => {
    const key = String(item || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    output.push(item);
  });
  return output;
};

const buildHouseRulePreview = (items = []) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length) return 'Tap to view details in the popup.';
  return list
    .slice(0, 2)
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .join(' - ');
};

const hasPolicyInfo = (data) => {
  if (!data) return false;
  const sections = data.house_rules_sections || data.house_rules;
  const hasSections = Array.isArray(sections)
    ? sections.length > 0
    : String(sections || '').trim().length > 0;
  return Boolean(
    hasSections ||
    data.pet_policy ||
    data.smoking_policy ||
    data.id_requirement ||
    data.guest_profile ||
    data.cancellation_policy ||
    data.refund_rules ||
    data.refund_policy ||
    data.security_deposit ||
    data.security_deposit_policy
  );
};

const MapPinIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.5 8.5c0 4.5-5.5 8-5.5 8S4.5 13 4.5 8.5a5.5 5.5 0 1 1 11 0Z"
    />
  </svg>
);

const CalendarIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <rect x="3.5" y="4.5" width="13" height="12" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 3.5v3M14 3.5v3M3.5 8.5h13" />
  </svg>
);

const UserIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 10a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 17a6.5 6.5 0 0 1 13 0" />
  </svg>
);

const MailIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 6.5h14v8H3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m3 6.5 7 5 7-5" />
  </svg>
);

const PhoneIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 3.5h3l1 3-2 1a8 8 0 0 0 3.5 3.5l1-2 3 1v3a1.5 1.5 0 0 1-1.5 1.5A10.5 10.5 0 0 1 4.5 5 1.5 1.5 0 0 1 6 3.5Z"
    />
  </svg>
);

const ShieldCheckIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 3 4.5 5v4.5c0 3.8 2.6 6.7 5.5 7.5 2.9-.8 5.5-3.7 5.5-7.5V5L10 3Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="m7.5 10 1.8 1.8L12.5 8.5" />
  </svg>
);

const ChevronDownIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
  </svg>
);

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const BookingReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stayData = useStoreSelector(useBookingStore, selectStayData);
  const stayId = useStoreSelector(useBookingStore, selectStayId);
  const checkIn = useStoreSelector(useBookingStore, selectCheckIn);
  const checkOut = useStoreSelector(useBookingStore, selectCheckOut);
  const nights = useStoreSelector(useBookingStore, selectNights);
  const guests = useStoreSelector(useBookingStore, selectGuests);
  const roomTypeId = useStoreSelector(useBookingStore, selectRoomTypeId);
  const roomCount = useStoreSelector(useBookingStore, selectRoomCount);
  const pricingBreakdown = useStoreSelector(useBookingStore, selectPricingBreakdown);
  const paymentOptionSelected = useStoreSelector(useBookingStore, selectPaymentOptionSelected);
  const setStayData = useStoreSelector(useBookingStore, selectSetStayData);
  const setPaymentOptionSelected = useStoreSelector(useBookingStore, selectSetPaymentOptionSelected);
  const { bookingId, draft, setBookingId, setDraft } = useBookingContext();
  const queryBookingId = String(searchParams.get('bookingId') || '').trim();
  const effectiveBookingId = bookingId || draft?.id || queryBookingId;
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [activeHouseRuleIndex, setActiveHouseRuleIndex] = useState(null);
  const houseRulesSectionRefs = useRef({});
  const loadedStayIdRef = useRef(null);

  useEffect(() => {
    if (queryBookingId && !bookingId) {
      setBookingId(queryBookingId);
    }
  }, [bookingId, queryBookingId, setBookingId]);

  useEffect(() => {
    if (draft?.id && !bookingId) {
      setBookingId(draft.id);
    }
  }, [bookingId, draft?.id, setBookingId]);

  useEffect(() => {
    if (!requireBookingId(effectiveBookingId, navigate)) return;
  }, [effectiveBookingId, navigate]);

  useEffect(() => {
    if (!effectiveBookingId || draft?.id || loading) return;
    let active = true;
    setLoading(true);
    getBookingById(effectiveBookingId)
      .then((booking) => {
        if (!active) return;
        const normalized = normalizeBookingDraft(booking, stayData);
        if (normalized?.id) setBookingId(normalized.id);
        setDraft(normalized);
      })
      .catch((err) => {
        if (!active) return;
        setPageError(err.message || 'Failed to load booking.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [effectiveBookingId, draft?.id, loading, setBookingId, setDraft, stayData]);

  useEffect(() => {
    if (!draft) return;
    requireGuestComplete(draft, navigate);
  }, [draft, navigate]);

  useEffect(() => {
    const effectiveStayId = draft?.stayId || stayId || stayData?.id || stayData?.slug;
    if (!effectiveStayId) return;
    const stayKey = String(effectiveStayId);
    const matchesStay =
      stayData && (String(stayData.id) === stayKey || String(stayData.slug) === stayKey);
    if (matchesStay && hasPolicyInfo(stayData)) {
      loadedStayIdRef.current = stayKey;
      return;
    }
    if (loadedStayIdRef.current === stayKey) return;
    loadedStayIdRef.current = stayKey;
    let active = true;
    const loadStay = async () => {
      try {
        const res = await getCheckoutStayData(stayKey);
        if (!active) return;
        const normalized = {
          ...res,
          id: res.id || res.property_id || stayKey,
          slug: res.slug || res.id || stayKey,
          coverImage: res.coverImage || res.featured_image || res.image_url,
        };
        setStayData(normalized);
      } catch (_) {
        // non-blocking
      }
    };
    loadStay();
    return () => {
      active = false;
    };
  }, [draft?.stayId, stayId, stayData, setStayData]);

  useEffect(() => {
    if (!infoModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setInfoModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [infoModalOpen]);

  useEffect(() => {
    if (!infoModalOpen || activeHouseRuleIndex === null) return;
    const node = houseRulesSectionRefs.current[activeHouseRuleIndex];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [infoModalOpen, activeHouseRuleIndex]);

  const pricing = useMemo(() => {
    const source = draft?.pricing || pricingBreakdown;
    if (!source) return null;
    const normalized = normalizeBookingPricing(source, {
      nights: draft?.nights || pricingBreakdown?.nights || nights || 0,
      currency: source.currency || stayData?.currency || 'INR',
    });
    return {
      base: normalized.basePrice,
      basePrice: normalized.basePrice,
      addons: toNumber(source.addons ?? pricingBreakdown?.addons),
      fees: normalized.mandatoryFees,
      mandatoryFees: normalized.mandatoryFees,
      tax: normalized.gstAmount,
      gstAmount: normalized.gstAmount,
      discount: normalized.discountAmount,
      discountAmount: normalized.discountAmount,
      taxableSubtotal: normalized.taxableSubtotal,
      total: normalized.finalPayable,
      finalPayable: normalized.finalPayable,
      currency: normalized.currency,
      breakdownItems: normalized.breakdownItems,
    };
  }, [draft?.nights, draft?.pricing, nights, pricingBreakdown, stayData?.currency]);

  const nightsLabel = draft?.nights || pricingBreakdown?.nights || nights || 0;
  const bookingMode = useMemo(() => {
    const hasRoomTypes = Array.isArray(stayData?.room_types) && stayData.room_types.length > 0;
    const hasRoomSelection = Boolean(roomTypeId || draft?.roomTypeId || draft?.roomType);
    return normalizeBookingMode(stayData?.booking_mode || stayData?.bookingMode, hasRoomTypes, hasRoomSelection);
  }, [stayData?.booking_mode, stayData?.bookingMode, stayData?.room_types, roomTypeId, draft?.roomTypeId, draft?.roomType]);
  const usesRooms = bookingMode === 'room';
  const selectedRoomType = useMemo(() => {
    if (!usesRooms) return null;
    const types = stayData?.room_types || [];
    if (roomTypeId && types.length) {
      return types.find((room) => String(room.id) === String(roomTypeId)) || null;
    }
    return draft?.roomType || null;
  }, [stayData, roomTypeId, draft, usesRooms]);
  const selectedRoomCount = usesRooms ? Number(roomCount || draft?.roomCount || 1) || 1 : 1;
  const taxFeeBreakdown = draft?.taxFeeBreakdown || pricingBreakdown?.taxFeeBreakdown || null;
  const lineItems = useMemo(() => {
    if (!pricing) return [];
    if (Array.isArray(pricing.breakdownItems) && pricing.breakdownItems.length) {
      return pricing.breakdownItems.map((item) => ({
        label: item.label,
        value: item.amount,
      }));
    }
    const rows = [
      {
        label: 'Base Price',
        value: pricing.basePrice,
      },
    ];
    if (pricing.discount) rows.push({ label: 'Discount', value: -Math.abs(pricing.discount) });
    rows.push({ label: 'Taxable Subtotal', value: pricing.taxableSubtotal });
    const taxFeeItems = getTaxFeeLineItems(taxFeeBreakdown);
    if (taxFeeItems.length) {
      rows.push(...taxFeeItems);
    } else {
      if (pricing.fees) rows.push({ label: 'Mandatory fees', value: pricing.fees });
      if (pricing.tax) rows.push({ label: 'GST', value: pricing.tax });
    }
    rows.push({ label: 'Final payable amount', value: pricing.finalPayable });
    return rows;
  }, [pricing, taxFeeBreakdown]);

  const totalPayable = pricing?.total || 0;
  const pricingCurrency = pricing?.currency || stayData?.currency || 'INR';
  const paymentOptions = useMemo(() => {
    if (draft?.paymentOptionSelected === 'partial' && draft?.partialPaymentPercentage) {
      return {
        full: {
          paymentOption: 'full',
          isAvailable: true,
          percentage: 100,
          payableNow: totalPayable,
          remainingLater: 0,
          currency: pricingCurrency,
        },
        partial: {
          paymentOption: 'partial',
          isAvailable: true,
          percentage: draft.partialPaymentPercentage,
          payableNow: draft.amountPaidNow,
          remainingLater: draft.remainingAmount,
          currency: pricingCurrency,
        },
      };
    }
    return pricingBreakdown?.paymentOptions || null;
  }, [draft, pricingBreakdown?.paymentOptions, pricingCurrency, totalPayable]);
  const partialPaymentOption = paymentOptions?.partial || null;
  const isPartialPaymentAvailable = Boolean(partialPaymentOption?.isAvailable);
  const selectedPaymentPreview =
    paymentOptionSelected === 'partial' && isPartialPaymentAvailable
      ? partialPaymentOption
      : paymentOptions?.full || {
          payableNow: totalPayable,
          remainingLater: 0,
        };
  const quoteTotal = Number(pricingBreakdown?.total);
  const bookingTotal = Number(draft?.pricing?.total);
  const hasQuoteTotal = Number.isFinite(quoteTotal);
  const hasBookingTotal = Number.isFinite(bookingTotal);
  const pricingDiff = hasQuoteTotal && hasBookingTotal ? bookingTotal - quoteTotal : 0;
  const debugPricing = searchParams.get('debugPricing') === '1';
  const showPricingDebug = debugPricing && hasQuoteTotal && hasBookingTotal;
  const guest = draft?.guestDetails || {};
  const stayPolicy = { ...(stayData || {}), ...(draft?.stay || {}) };
  const houseRulesSections = useMemo(() => {
    const baseSections = normalizeHouseRulesSections(stayPolicy?.house_rules_sections, stayPolicy?.house_rules);
    return mergeHouseRulesFromPolicies(baseSections, {
      petPolicy: stayPolicy?.pet_policy,
      smokingPolicy: stayPolicy?.smoking_policy,
      idRequirement: stayPolicy?.id_requirement,
      guestProfile: stayPolicy?.guest_profile,
    });
  }, [
    stayPolicy?.house_rules_sections,
    stayPolicy?.house_rules,
    stayPolicy?.pet_policy,
    stayPolicy?.smoking_policy,
    stayPolicy?.id_requirement,
    stayPolicy?.guest_profile,
  ]);
  const cancellationItems = useMemo(
    () => dedupeTextList(normalizeTextList(stayPolicy?.cancellation_policy)),
    [stayPolicy?.cancellation_policy]
  );
  const refundItems = useMemo(
    () => dedupeTextList(normalizeTextList(stayPolicy?.refund_rules || stayPolicy?.refund_policy)),
    [stayPolicy?.refund_rules, stayPolicy?.refund_policy]
  );
  const securityDepositAmount = Number(stayPolicy?.security_deposit || 0);
  const securityDepositNotes = useMemo(
    () => dedupeTextList(normalizeTextList(stayPolicy?.security_deposit_policy)),
    [stayPolicy?.security_deposit_policy]
  );
  const hasSecurityDeposit = securityDepositAmount > 0 || securityDepositNotes.length > 0;
  const hasHouseRules = houseRulesSections.length > 0;
  const stayTitle = stayData?.title || stayData?.name || draft?.stayName || 'Homevilas stay';
  const propertyGuestCapacity = stayData?.max_guests || draft?.stay?.max_guests || stayPolicy?.max_guests || 0;
  const stayLocation =
    [stayData?.city, stayData?.area].filter(Boolean).join(', ') ||
    draft?.location ||
    stayData?.location ||
    'Location coming soon';
  const stayImage =
    stayData?.coverImage ||
    stayData?.featured_image ||
    stayData?.image_url ||
    'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80';
  const stayDateRange = formatDateRange(draft?.checkIn || checkIn, draft?.checkOut || checkOut);
  const stayGuestsLabel = formatGuests(draft?.guests || guests);
  const stayNightsLabel = `${nightsLabel || 0} night${nightsLabel === 1 ? '' : 's'}`;
  const checkInDate = draft?.checkIn || checkIn;
  const checkOutDate = draft?.checkOut || checkOut;
  const nightsCount = Number(nightsLabel || 0);
  const daysCount = nightsCount ? nightsCount + 1 : 0;
  const nightsCountLabel = nightsCount ? `${nightsCount} night${nightsCount === 1 ? '' : 's'}` : '-';
  const daysCountLabel = daysCount ? `${daysCount} day${daysCount === 1 ? '' : 's'}` : '-';
  const stayBadges = ['Entire Villa', 'Private Pool'];
  const stayHighlights = useMemo(() => {
    const items = [];
    if (stayData?.bedrooms) items.push(`${stayData.bedrooms} BHK`);
    if (stayData?.max_guests) items.push(`Up to ${stayData.max_guests} guests`);
    if (usesRooms && selectedRoomType?.name) items.push(selectedRoomType.name);
    if (selectedRoomCount > 1) items.push(`${selectedRoomCount} rooms`);
    return items.filter(Boolean).slice(0, 3);
  }, [stayData?.bedrooms, stayData?.max_guests, usesRooms, selectedRoomType?.name, selectedRoomCount]);
  const bookingMeta = [stayDateRange, stayNightsLabel, stayGuestsLabel].filter(Boolean);
  const hasStay = Boolean(stayData || draft?.stayName || draft?.stay);
  const selectedPaymentTitle =
    paymentOptionSelected === 'partial' && isPartialPaymentAvailable
      ? `Partial payment (${partialPaymentOption.percentage}% now)`
      : 'Full payment';
  const selectedPaymentDescription =
    paymentOptionSelected === 'partial' && isPartialPaymentAvailable
      ? 'Pay the advance today and settle the remaining amount at check-in.'
      : 'Pay the complete booking amount now to confirm the stay in one step.';
  const policySnapshot = {
    stayTitle,
    cancellationItems,
    refundItems,
  };
  const policyQuery = effectiveBookingId ? `?bookingId=${encodeURIComponent(effectiveBookingId)}` : '';
  const savePolicySnapshot = () => {
    try {
      sessionStorage.setItem('homevilas.bookingPolicySnapshot', JSON.stringify(policySnapshot));
    } catch (_) {
      // non-blocking
    }
  };

  useEffect(() => {
    if (!isPartialPaymentAvailable && paymentOptionSelected === 'partial') {
      setPaymentOptionSelected('full');
      return;
    }
    if (draft?.paymentOptionSelected === 'full') {
      setPaymentOptionSelected('full');
      return;
    }
    if (draft?.paymentOptionSelected === 'partial' && isPartialPaymentAvailable) {
      setPaymentOptionSelected(draft.paymentOptionSelected);
    }
  }, [draft?.paymentOptionSelected, isPartialPaymentAvailable, paymentOptionSelected, setPaymentOptionSelected]);

  const openHouseRulesModal = (index = null) => {
    if (!houseRulesSections.length) return;
    setActiveHouseRuleIndex(Number.isInteger(index) ? index : null);
    setInfoModalOpen(true);
  };

  const handleBack = () =>
    navigate(
      effectiveBookingId
        ? `/booking/guest?bookingId=${encodeURIComponent(effectiveBookingId)}`
        : '/booking/guest'
    );

  const handleConfirm = async () => {
    if (loading) return;
    setPageError('');
    const targetBookingId = effectiveBookingId;
    if (!targetBookingId) {
      setPageError('Booking details are missing. Please return to the guest page.');
      return;
    }
    if (!pricing) {
      setPageError('Pricing is still loading. Please try again in a moment.');
      return;
    }
    setLoading(true);
    try {
      const booking = await confirmBooking(targetBookingId, undefined, {
        paymentOptionSelected,
      });
      const normalized = {
        ...normalizeBookingDraft(booking, stayData),
        paymentOptionSelected,
      };
      const nextBookingId = normalized?.id || targetBookingId;
      if (normalized?.id) setBookingId(normalized.id);
      setDraft(normalized);
      navigate(`/booking/payment?bookingId=${encodeURIComponent(nextBookingId)}`);
    } catch (err) {
      setPageError(err.message || 'Unable to confirm booking.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="hv-mobile-only hv-ref-booking-screen hv-ref-review-mobile">
      <main className="hv-ref-booking-main">
        <section className="hv-ref-booking-card">
          <div className="hv-ref-booking-row">
            <span>Step 2 of 2 · Review stay, policies & payment</span>
            <button type="button" onClick={handleBack}>‹ Back</button>
          </div>
          <h1>Review your reservation</h1>
          <div className="hv-ref-review-rows">
            <div><span>Stay</span><strong>{stayTitle}</strong></div>
            <div><span>Dates</span><strong>{formatShortDate(checkInDate)} → {formatShortDate(checkOutDate)} · {stayNightsLabel}</strong></div>
            <div><span>Primary traveller</span><strong>{guest.fullName || guest.title || '-'}</strong></div>
            <div><span>Guests</span><strong>{stayGuestsLabel}</strong></div>
            <div><span>Arrival</span><strong>{guest.arrivalTime || '2 PM – 4 PM'}</strong></div>
          </div>
          <button className="hv-ref-secondary-btn" type="button" onClick={handleBack}>✎ Edit details</button>
        </section>

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Important information & policies</div>
          <div className="hv-ref-policies">
            <details open>
              <summary>Charges</summary>
              {hasSecurityDeposit ? (
                <ul>
                  {securityDepositAmount > 0 ? <li>Security deposit: {formatInr(securityDepositAmount, pricingCurrency)} is collected as per property rules.</li> : null}
                  {securityDepositNotes.map((note, index) => <li key={index}>{note}</li>)}
                </ul>
              ) : <p>No extra charges listed for this stay.</p>}
            </details>
            <details>
              <summary>Cancellation</summary>
              {cancellationItems.length ? <ul>{cancellationItems.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Cancellation policy will be shared soon.</p>}
              <Link to={`/booking/policy/cancellation${policyQuery}`} onClick={savePolicySnapshot}>Open cancellation policy</Link>
            </details>
            <details>
              <summary>Refund</summary>
              {refundItems.length ? <ul>{refundItems.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Refund rules will be shared soon.</p>}
              <Link to={`/booking/policy/refund${policyQuery}`} onClick={savePolicySnapshot}>Open refund policy</Link>
            </details>
          </div>
        </section>

        <section className="hv-ref-booking-card">
          <div className="hv-ref-eyebrow">Choose payment option</div>
          <div className="hv-ref-pay-options">
            <button type="button" className={paymentOptionSelected === 'full' ? 'selected' : ''} onClick={() => setPaymentOptionSelected('full')}>
              <span>Pay full amount</span>
              <strong>{formatInr(totalPayable, pricingCurrency)}</strong>
              <small>Pay 100% now to confirm the stay in one step.</small>
            </button>
            {isPartialPaymentAvailable ? (
              <button type="button" className={paymentOptionSelected === 'partial' ? 'selected' : ''} onClick={() => setPaymentOptionSelected('partial')}>
                <span>Pay {partialPaymentOption.percentage}% deposit</span>
                <strong>{formatInr(partialPaymentOption.payableNow, pricingCurrency)}</strong>
                <small>Balance is collected as per property payment policy.</small>
              </button>
            ) : null}
          </div>
          {pageError ? <p className="hv-flow-error" role="alert">{pageError}</p> : null}
        </section>
      </main>
      <div className="hv-ref-bar">
        <button className="hv-ref-total" type="button"><span>Grand total</span><strong>{formatInr(selectedPaymentPreview.payableNow || totalPayable, pricingCurrency)}</strong></button>
        <button className="hv-ref-book" type="button" onClick={handleConfirm} disabled={loading}>{loading ? 'Confirming…' : 'Payment →'}</button>
      </div>
    </div>
    <div className="hv-desktop-flow hv-review-page relative min-h-screen bg-[#FAF7F2] pb-24 text-[#243444]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-0 h-80 w-80 rounded-full bg-[#F4E7CF] opacity-70 blur-3xl" />
        <div className="absolute left-0 top-24 h-72 w-72 rounded-full bg-[#DDF1EE] opacity-60 blur-3xl" />
      </div>
      <style>{`
        .hv-review-page h1,
        .hv-review-page h2,
        .hv-review-page h3 {
          color: #10233D;
        }
        .hv-house-rules-modal {
          width: min(960px, 94vw);
          max-height: 82vh;
          display: flex;
          flex-direction: column;
        }
        .hv-house-rules-body {
          overflow: auto;
          padding-right: 6px;
        }
        .hv-house-rules-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }
        .hv-house-rule-card {
          text-align: left;
          border-radius: 18px;
          border: 1px solid #E7DED4;
          background: #fff;
          padding: 14px 16px;
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .hv-house-rule-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
          border-color: rgba(15, 92, 90, 0.35);
        }
        .hv-house-rule-count {
          font-size: 0.65rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #0F5C5A;
          background: #DDF1EE;
          border: 1px solid rgba(15, 92, 90, 0.2);
          border-radius: 999px;
          padding: 2px 8px;
          white-space: nowrap;
        }
        .hv-house-rules-stack {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .hv-house-rule-section {
          border-radius: 16px;
          border: 1px solid #E7DED4;
          background: #fff;
          padding: 14px 16px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
        }
        .hv-house-rule-section.active {
          border-color: rgba(15, 92, 90, 0.45);
          background: #DDF1EE;
          box-shadow: 0 10px 22px rgba(15, 92, 90, 0.12);
        }
      `}</style>
      <div className="relative mx-auto flex max-w-6xl flex-col gap-8 px-4 py-12">
        <div className="hv-review-legacy-crumb-row flex flex-wrap items-center justify-between gap-4">
          <nav className="text-xs uppercase tracking-[0.32em] text-[#8B97A7]">
            <Link to="/" className="hover:text-[#10233D]">
              Home
            </Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/listings" className="hover:text-[#10233D]">
              Stays
            </Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-[#10233D]">Review</span>
          </nav>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8B97A7]">
            {['Search', 'Details', 'Review', 'Payment'].map((step, index, arr) => {
              const isActive = step === 'Review';
              return (
                <div key={step} className="flex items-center gap-2">
                  <span className={isActive ? 'text-[#0F5C5A]' : ''}>{step}</span>
                  {index < arr.length - 1 && <span className="h-[1px] w-6 bg-[#D8E3E0]" />}
                </div>
              );
            })}
          </div>
        </div>
        <div className="animate-fade-up rounded-[24px] border border-[#E6DED1] bg-white p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#8B97A7]">Review your booking</p>
              <h1 className="mt-2 font-homevilas text-4xl font-semibold text-[#10233D]">
                {effectiveBookingId ? `#${effectiveBookingId}` : 'Draft booking'}
              </h1>
            </div>
            {bookingMeta.length ? (
              <div className="flex items-center gap-2 text-sm text-[#667085]">
                <CalendarIcon className="h-4 w-4 text-[#8B97A7]" />
                <span>{bookingMeta.join(' | ')}</span>
              </div>
            ) : null}
          </div>
        </div>
        {pageError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{pageError}</div>
        )}
        <div className="grid gap-8 lg:grid-cols-[1.85fr_1fr]">
          <div className="space-y-8 animate-fade-up-delay-1">
            {hasStay ? (
              <div className="rounded-[24px] border border-[#E6DED1] bg-white p-7 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
                <div className="flex flex-col gap-6 md:flex-row">
                  <div className="relative h-52 w-full overflow-hidden rounded-2xl md:h-44 md:w-60">
                    <img src={stayImage} alt={stayTitle} className="h-full w-full object-cover" />
                    <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                      {stayBadges.map((badge) => (
                        <span
                          key={badge}
                          className="rounded-full bg-[#F4E7CF] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A6428] shadow-sm"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.35em] text-[#8B97A7]">Your stay</p>
                      <h2 className="mt-2 font-homevilas text-[34px] font-semibold leading-none text-[#10233D]">{stayTitle}</h2>
                      <div className="mt-2 flex items-center gap-2 text-sm text-[#667085]">
                        <MapPinIcon className="h-4 w-4 text-[#0F5C5A]" />
                        <span>{stayLocation}</span>
                      </div>
                    </div>
                    <div className="mt-4 grid gap-3 rounded-[20px] border border-[#E6DED1] bg-[#FFFDFC] p-4 text-sm text-[#243444] sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Check in</p>
                        <p className="mt-1 font-semibold text-[#10233D]">{formatShortDate(checkInDate)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Check out</p>
                        <p className="mt-1 font-semibold text-[#10233D]">{formatShortDate(checkOutDate)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">No of nights</p>
                        <p className="mt-1 font-semibold text-[#10233D]">{nightsCountLabel}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">No of days</p>
                        <p className="mt-1 font-semibold text-[#10233D]">{daysCountLabel}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Guests selected</p>
                        <p className="mt-1 font-semibold text-[#10233D]">{stayGuestsLabel}</p>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-[#667085]">
                      {propertyGuestCapacity
                        ? `Property accommodates up to ${propertyGuestCapacity} guest${propertyGuestCapacity === 1 ? '' : 's'}.`
                        : 'Property guest capacity will be shared soon.'}
                    </div>
                    {stayHighlights.length ? (
                      <div className="flex flex-wrap gap-2">
                        {stayHighlights.map((item) => (
                          <span
                            key={item}
                            className="rounded-full bg-[#F4E7CF] px-3 py-1 text-xs font-semibold text-[#8A6428]"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-[#E6DED1] bg-white/80 p-6 text-sm text-[#667085]">
                Stay details will appear once a selection is made.
              </div>
            )}
            <div className="rounded-[24px] border border-[#E6DED1] bg-white p-7 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xl font-semibold text-[#10233D]">Guest details</h2>
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#8B97A7]">Primary guest</span>
              </div>
              <div className="mt-5 grid gap-4 text-sm text-[#243444] sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4E7CF] text-[#8A6428]">
                    <UserIcon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Name</p>
                    <p className="font-semibold text-[#10233D]">{guest.fullName || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4E7CF] text-[#8A6428]">
                    <MailIcon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Email</p>
                    <p className="font-semibold text-[#10233D]">{guest.email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4E7CF] text-[#8A6428]">
                    <PhoneIcon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Phone</p>
                    <p className="font-semibold text-[#10233D]">{guest.phone || '-'}</p>
                  </div>
                </div>
                {guest.arrivalTime && (
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4E7CF] text-[#8A6428]">
                      <CalendarIcon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.24em] text-[#8B97A7]">Arrival</p>
                      <p className="font-semibold text-[#10233D]">{guest.arrivalTime}</p>
                    </div>
                  </div>
                )}
              </div>
              {guest.specialRequests && (
                <div className="mt-5 rounded-xl border border-[#E6DED1] bg-[#FFFDFC] px-4 py-3 text-xs text-[#667085]">
                  {guest.specialRequests}
                </div>
              )}
            </div>
            <div className="rounded-[24px] border border-[#E6DED1] bg-white p-7 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="space-y-1">
                <h2 className="text-xl font-semibold text-[#10233D]">Important information</h2>
                <p className="text-sm text-[#667085]">Extra charges to be paid directly at the cottage.</p>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <Link
                  to={`/booking/policy/cancellation${policyQuery}`}
                  onClick={savePolicySnapshot}
                  className="rounded-2xl border border-[#E6DED1] bg-[#FFFDFC] px-4 py-3 text-sm font-semibold text-[#0F5C5A] hover:border-[#0F5C5A]"
                >
                  Open cancellation policy
                </Link>
                <Link
                  to={`/booking/policy/refund${policyQuery}`}
                  onClick={savePolicySnapshot}
                  className="rounded-2xl border border-[#E6DED1] bg-[#FFFDFC] px-4 py-3 text-sm font-semibold text-[#0F5C5A] hover:border-[#0F5C5A]"
                >
                  Open refund policy
                </Link>
              </div>
              <div className="mt-5 space-y-3">
                <details open className="group rounded-2xl border border-[#E6DED1] bg-[#FFFDFC] px-4 py-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[#10233D]">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#C9A15B]" />
                      Mandatory charges
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-[#8B97A7] transition group-open:rotate-180" />
                  </summary>
                  <div className="mt-4">
                    {hasSecurityDeposit ? (
                      <div className="rounded-xl border border-[#E6DED1] bg-white px-4 py-3 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="text-sm font-semibold text-[#10233D]">Security Deposit</div>
                          {securityDepositAmount > 0 && (
                          <div className="text-lg font-semibold text-[#10233D]">{formatInr(securityDepositAmount, pricingCurrency)}</div>
                          )}
                        </div>
                        {securityDepositNotes.length ? (
                          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-[#667085]">
                            {securityDepositNotes.map((note, index) => (
                              <li key={`${note}-${index}`}>{note}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
                        <ShieldCheckIcon className="h-4 w-4" />
                        No extra charges listed for this stay.
                      </div>
                    )}
                  </div>
                </details>
                <details className="group rounded-2xl border border-[#E6DED1] bg-white px-4 py-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[#10233D]">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#C9A15B]" />
                      Cancellation policy
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-[#8B97A7] transition group-open:rotate-180" />
                  </summary>
                  <div className="mt-4">
                    {cancellationItems.length ? (
                      <ul className="list-disc space-y-1 pl-5 text-sm text-[#243444]">
                        {cancellationItems.map((item, index) => (
                          <li key={`${item}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-[#667085]">Cancellation policy will be shared soon.</div>
                    )}
                    <Link
                      to={`/booking/policy/cancellation${policyQuery}`}
                      onClick={savePolicySnapshot}
                      className="mt-4 inline-flex rounded-full border border-[#E6DED1] px-4 py-2 text-xs font-semibold text-[#0F5C5A] hover:border-[#0F5C5A]"
                    >
                      Open cancellation policy
                    </Link>
                  </div>
                </details>
                <details className="group rounded-2xl border border-[#E6DED1] bg-white px-4 py-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-[#10233D]">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#C9A15B]" />
                      Refund rules
                    </span>
                    <ChevronDownIcon className="h-4 w-4 text-[#8B97A7] transition group-open:rotate-180" />
                  </summary>
                  <div className="mt-4">
                    {refundItems.length ? (
                      <ul className="list-disc space-y-1 pl-5 text-sm text-[#243444]">
                        {refundItems.map((item, index) => (
                          <li key={`${item}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-[#667085]">Refund rules will be shared soon.</div>
                    )}
                    <Link
                      to={`/booking/policy/refund${policyQuery}`}
                      onClick={savePolicySnapshot}
                      className="mt-4 inline-flex rounded-full border border-[#E6DED1] px-4 py-2 text-xs font-semibold text-[#0F5C5A] hover:border-[#0F5C5A]"
                    >
                      Open refund policy
                    </Link>
                  </div>
                </details>
                <div>
                  {hasHouseRules ? (
                    <details open className="group rounded-2xl border border-[#E6DED1] bg-white px-4 py-3">
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
                        <div>
                          <div className="text-base font-semibold text-[#10233D]">House Rules & Information</div>
                          <div className="text-xs text-[#667085]">Please review the house rules before confirming.</div>
                        </div>
                        <ChevronDownIcon className="mt-1 h-4 w-4 text-[#8B97A7] transition group-open:rotate-180" />
                      </summary>
                      <div className="mt-4">
                        <div className="flex flex-wrap items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => openHouseRulesModal()}
                            className="rounded-full border border-[#E6DED1] px-4 py-1.5 text-xs font-semibold text-[#667085] hover:border-[#C9A15B] hover:text-[#10233D]"
                          >
                            View all house rules
                          </button>
                        </div>
                        <div className="mt-3 hv-house-rules-grid">
                          {houseRulesSections.map((section, index) => {
                            const items = Array.isArray(section.items) ? section.items : [];
                            return (
                              <button
                                key={`${section.title || 'house-rule'}-${index}`}
                                type="button"
                                className="hv-house-rule-card"
                                onClick={() => openHouseRulesModal(index)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="font-semibold text-[#10233D]">
                                    {section.title || `Section ${index + 1}`}
                                  </div>
                                  <span className="hv-house-rule-count">
                                    {items.length ? `${items.length} rules` : 'Details'}
                                  </span>
                                </div>
                                <div className="mt-1 text-xs text-[#667085]">{buildHouseRulePreview(items)}</div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-2 text-xs text-[#667085]">Tap a section to view the full house rules.</div>
                      </div>
                    </details>
                  ) : (
                    <div className="rounded-xl border border-[#E6DED1] bg-[#FFFDFC] px-3 py-3 text-sm text-[#667085]">
                      House rules will be shared soon.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-[24px] border border-[#E6DED1] bg-white px-5 py-4 text-sm text-[#667085] shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
              Need help? Call +918287703118 or email info@homevilas.com, meena@homevilas.com
            </div>
          </div>
          <div className="space-y-6 lg:sticky lg:top-24 lg:self-start animate-fade-up-delay-2">
            {showPricingDebug ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 shadow-[0_12px_24px_rgba(15,23,42,0.08)]">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700">Pricing debug</div>
                <div className="mt-1 grid gap-1">
                  <div>Quote total: {formatInr(quoteTotal, pricingCurrency)}</div>
                  <div>Booking total: {formatInr(bookingTotal, pricingCurrency)}</div>
                  <div>Diff: {formatInr(pricingDiff, pricingCurrency)}</div>
                </div>
              </div>
            ) : null}
            <div className="rounded-[24px] border border-[#E6DED1] bg-white p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 overflow-hidden rounded-xl">
                  <img src={stayImage} alt={stayTitle} className="h-full w-full object-cover" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#8B97A7]">Stay summary</p>
                  <p className="mt-1 text-lg font-semibold text-[#10233D]">{stayTitle}</p>
                  <p className="text-xs text-[#667085]">{stayLocation}</p>
                </div>
              </div>
              {bookingMeta.length ? (
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#EEE6DB] pb-4 text-xs text-[#667085]">
                  <span className="flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5 text-[#8B97A7]" />
                    {stayDateRange}
                  </span>
                  <span className="h-1 w-1 rounded-full bg-[#C9A15B]" />
                  <span>{stayNightsLabel}</span>
                  <span className="h-1 w-1 rounded-full bg-[#C9A15B]" />
                  <span>{stayGuestsLabel}</span>
                </div>
              ) : null}
              <div className="mt-6 space-y-3 text-sm text-[#243444]">
                {lineItems.length ? (
                  lineItems.map((item) => {
                    const numericValue = Number(item.value || 0);
                    return (
                      <div key={item.label} className="flex items-center justify-between">
                        <span className="text-[#667085]">{item.label}</span>
                        <span className={numericValue < 0 ? 'font-semibold text-emerald-600' : 'font-semibold text-[#10233D]'}>
                          {formatInr(item.value, pricingCurrency)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-[#667085]">Pricing breakdown will appear here.</div>
                )}
              </div>
              <div className="mt-6 rounded-[20px] border border-[#E6DED1] bg-[#FFFDFC] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#8B97A7]">Payment option</div>
                  <span className="rounded-full bg-[#F4E7CF] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8A6428]">
                    {paymentOptionSelected === 'partial' && isPartialPaymentAvailable ? 'Advance now' : 'Secure now'}
                  </span>
                </div>
                <div className="mt-3 grid gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentOptionSelected('full')}
                    className={`flex w-full items-start justify-between rounded-xl border px-4 py-3 text-left transition ${
                      paymentOptionSelected === 'full'
                        ? 'border-[#0F5C5A] bg-[#DDF1EE] shadow-sm'
                        : 'border-[#E6DED1] bg-white'
                    }`}
                  >
                    <div>
                      <div className="text-sm font-semibold text-[#10233D]">Pay full amount</div>
                      <div className="mt-1 text-[11px] text-[#667085]">One secure payment today.</div>
                    </div>
                    <div className="text-sm font-semibold text-[#10233D]">
                      {formatInr(totalPayable, pricingCurrency)}
                    </div>
                  </button>
                  {isPartialPaymentAvailable ? (
                    <button
                      type="button"
                      onClick={() => setPaymentOptionSelected('partial')}
                      className={`flex w-full items-start justify-between rounded-xl border px-4 py-3 text-left transition ${
                        paymentOptionSelected === 'partial'
                          ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                          : 'border-[#E6DED1] bg-white'
                      }`}
                    >
                      <div>
                        <div className="text-sm font-semibold text-[#10233D]">
                          Pay partial amount ({partialPaymentOption.percentage}%)
                        </div>
                        <div className="mt-1 text-[11px] text-[#667085]">Advance now, balance at check-in.</div>
                      </div>
                      <div className="text-sm font-semibold text-emerald-700">
                        {formatInr(partialPaymentOption.payableNow, pricingCurrency)}
                      </div>
                    </button>
                  ) : null}
                </div>
                <div
                  className={`mt-3 rounded-[18px] border px-4 py-4 ${
                    paymentOptionSelected === 'partial' && isPartialPaymentAvailable
                      ? 'border-emerald-100 bg-emerald-50'
                      : 'border-[#E6DED1] bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8B97A7]">
                        Selected option
                      </div>
                      <div className="mt-1 text-sm font-semibold text-[#10233D]">{selectedPaymentTitle}</div>
                      <div className="mt-1 text-xs text-[#667085]">{selectedPaymentDescription}</div>
                    </div>
                    <div className="text-sm font-semibold text-[#10233D]">
                      {formatInr(selectedPaymentPreview.payableNow, pricingCurrency)}
                    </div>
                  </div>
                  {paymentOptionSelected === 'partial' && isPartialPaymentAvailable ? (
                    <div className="mt-4 grid gap-2 border-t border-emerald-100 pt-3 text-sm text-emerald-900">
                      <div className="flex items-center justify-between">
                        <span>Pay now</span>
                        <span className="font-semibold">
                          {formatInr(selectedPaymentPreview.payableNow, pricingCurrency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Pay at check-in</span>
                        <span className="font-semibold">
                          {formatInr(selectedPaymentPreview.remainingLater, pricingCurrency)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-5 rounded-[18px] border border-[#E6DED1] bg-[#F6F1E8] px-4 py-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#667085]">Total payable</span>
                  <span className="text-2xl font-semibold text-[#0A4443]">{formatInr(totalPayable, pricingCurrency)}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-[#667085]">
                  <span>Amount to pay now</span>
                  <span className="font-semibold text-[#10233D]">
                    {formatInr(selectedPaymentPreview.payableNow, pricingCurrency)}
                  </span>
                </div>
                {paymentOptionSelected === 'partial' && isPartialPaymentAvailable ? (
                  <div className="mt-1 flex items-center justify-between text-sm text-[#667085]">
                    <span>Pay at check-in</span>
                    <span className="font-semibold text-[#10233D]">
                      {formatInr(selectedPaymentPreview.remainingLater, pricingCurrency)}
                    </span>
                  </div>
                ) : null}
                <div className="mt-2 text-xs text-[#667085]">All prices are in {pricingCurrency}.</div>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="hv-review-main-payment-cta mt-6 w-full rounded-full bg-[#0F5C5A] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[#0F5C5A]/20 transition hover:bg-[#0A4443] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Confirming...' : 'Proceed to payment'}
              </button>
              <div className="mt-4 grid gap-2 text-xs text-[#667085]">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                  100% secure payment
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                  No hidden charges
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                  Instant confirmation
                </div>
              </div>
              <button
                type="button"
                onClick={handleBack}
                className="mt-5 w-full rounded-full border border-[#E6DED1] px-4 py-2 text-xs font-semibold text-[#667085] hover:border-[#C9A15B] hover:text-[#10233D]"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
      {infoModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setInfoModalOpen(false)}
        >
          <div
            className="hv-house-rules-modal overflow-hidden rounded-3xl bg-white shadow-[0_30px_70px_rgba(15,23,42,0.3)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative border-b border-[#E6DED1] bg-gradient-to-r from-[#F6F1E8] via-white to-[#F4E7CF] px-6 py-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#8A6428]">
                House rules
              </div>
              <h3 className="mt-1 font-homevilas text-3xl font-semibold text-[#10233D]">House Rules & Information</h3>
              <p className="mt-1 text-sm text-[#667085]">
                Please review the house rules before confirming your stay.
              </p>
              <button
                type="button"
                onClick={() => setInfoModalOpen(false)}
                className="absolute right-4 top-4 rounded-full border border-[#E6DED1] bg-white px-3 py-1 text-xs font-semibold text-[#667085] hover:border-[#C9A15B]"
              >
                Close
              </button>
            </div>
            <div className="hv-house-rules-body px-6 py-5">
              {houseRulesSections.length ? (
                <div className="hv-house-rules-stack">
                  {houseRulesSections.map((section, index) => {
                    const items = normalizeTextList(section.items);
                    if (!items.length) return null;
                    return (
                      <div
                        key={`${section.title || 'house-rule'}-${index}`}
                        ref={(node) => {
                          if (node) houseRulesSectionRefs.current[index] = node;
                        }}
                        className={`hv-house-rule-section${activeHouseRuleIndex === index ? ' active' : ''}`}
                      >
                        <div className="text-base font-semibold text-[#10233D]">
                          {section.title || `Section ${index + 1}`}
                        </div>
                        <div className="text-xs text-[#667085]">{items.length} rules</div>
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[#243444]">
                          {items.map((item, idx) => (
                            <li key={`${section.title || 'rule'}-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-[#667085]">House rules will be shared soon.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      <MobileStickyBar
        total={selectedPaymentPreview.payableNow || totalPayable}
        lineItems={lineItems}
        currency={pricingCurrency}
        onContinue={handleConfirm}
        disabled={loading}
        label={loading ? 'Confirming' : 'Proceed to payment'}
        onBack={handleBack}
        backLabel="Back"
      />
    </div>
    </>
  );
};

export default BookingReview;
